import { Navigate, Route, Routes } from 'react-router-dom';
import { LANDING_V2 } from './landing.config';
import './styles/theme.css';
import './styles/landing.css';
import SiteShell from './components/layout/SiteShell';
import LandingPage from './LandingPage';

import MonitoringPage from './pages/MonitoringPage';
import SolutionsPage from './pages/SolutionsPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import HrmsPage from './pages/HrmsPage';
import CrmPage from './pages/CrmPage';
import EmailInsightsPage from './pages/EmailInsightsPage';
import PatternIntelligencePage from './pages/PatternIntelligencePage';
import BookDemoPage from './pages/BookDemoPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import CareersPage from './pages/CareersPage';
import ComparePage from './pages/ComparePage';
import HelpPage from './pages/HelpPage';
import PartnersPage from './pages/PartnersPage';
import PrivacyPage from './pages/PrivacyPage';
import SecurityPage from './pages/SecurityPage';
import TermsPage from './pages/TermsPage';

/* feature pages */
import FeaturesPage from './pages/features/FeaturesPage';
import AskTeamGridPage from './pages/features/AskTeamGridPage';
import AutomaticTimeTrackingPage from './pages/features/AutomaticTimeTrackingPage';
import ProductivityScoringPage from './pages/features/ProductivityScoringPage';
import AIWorkSummariesPage from './pages/features/AIWorkSummariesPage';
import AppWebsiteTrackingPage from './pages/features/AppWebsiteTrackingPage';
import HourlyBreakdownPage from './pages/features/HourlyBreakdownPage';
import TeamManagementPage from './pages/features/TeamManagementPage';
import ProjectTrackingPage from './pages/features/ProjectTrackingPage';
import OrgIntelligencePage from './pages/features/OrgIntelligencePage';

/* solutions pages */
import RemoteTeamsPage from './pages/solutions/RemoteTeamsPage';
import HRStaffingPage from './pages/solutions/HRStaffingPage';
import EdTechPage from './pages/solutions/EdTechPage';
import AccountingPage from './pages/solutions/AccountingPage';
import ProjectBasedPage from './pages/solutions/ProjectBasedPage';
import VirtualSupportPage from './pages/solutions/VirtualSupportPage';
import OperationsPage from './pages/solutions/OperationsPage';
import BPOPage from './pages/solutions/BPOPage';
import ContractFreelancePage from './pages/solutions/ContractFreelancePage';
import ClientDrivenPage from './pages/solutions/ClientDrivenPage';

/* comparison pages */
import WorkforcePlatformPage from './pages/compare/WorkforcePlatformPage';
import We360Page from './pages/compare/We360Page';
import InsightfulPage from './pages/compare/InsightfulPage';
import ProHancePage from './pages/compare/ProHancePage';
import TimeDoctorPage from './pages/compare/TimeDoctorPage';
import HubstaffPage from './pages/compare/HubstaffPage';
import ActivTrakPage from './pages/compare/ActivTrakPage';

/* SEO landings */
import EmployeeMonitoringSoftwarePage from './pages/EmployeeMonitoringSoftwarePage';
import TimeTrackingSoftwarePage from './pages/TimeTrackingSoftwarePage';

/* conversion */
import ResourceOptimizerPage from './pages/ResourceOptimizerPage';
import ResourceOptimizerBookPage from './pages/ResourceOptimizerBookPage';
import BookDemoConfirmPage from './pages/BookDemoConfirmPage';
import ThanksDemoRequestPage from './pages/ThanksDemoRequestPage';
import ThanksContactPage from './pages/ThanksContactPage';

/* auth & account */
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import InvitePage from './pages/InvitePage';
import PartnerLoginPage from './pages/PartnerLoginPage';

/* misc */
import DownloadAllPage from './pages/DownloadAllPage';

const To = ({ to }) => <Navigate to={to} replace />;

/**
 * All landing-site routes, WITHOUT a Router.
 *
 * Don't mount this directly — use LandingSwitch instead. Mounting it as a
 * splat route beside the host's own routes looks like it should work, but
 * React Router v6 ranks by specificity, so the host's own "/" and "/login"
 * would always win and these pages would never render.
 *
 * Every path declared below must also appear in routes.manifest.js;
 * `npm run check:routes` enforces that.
 */
export default function LandingRoutes() {
  /* Belt and braces: <LandingSwitch> already refuses to render this when the
     flag is off, but anyone mounting LandingRoutes directly gets the same
     guarantee — flag off means the new site renders nothing at all. */
  if (!LANDING_V2) return null;

  return (
      <Routes>
        <Route element={<SiteShell />}>
          <Route index element={<LandingPage />} />

          {/* product */}
          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="download" element={<DownloadAllPage />} />
          <Route path="download/all" element={<DownloadAllPage />} />
          <Route path="hrms" element={<HrmsPage />} />
          <Route path="crm" element={<CrmPage />} />
          <Route path="email-insights" element={<EmailInsightsPage />} />
          <Route path="pattern-intelligence" element={<PatternIntelligencePage />} />
          <Route path="book-demo" element={<BookDemoPage />} />
          <Route path="book-demo/confirm" element={<BookDemoConfirmPage />} />

          {/* features */}
          <Route path="features" element={<FeaturesPage />} />
          <Route path="features/ask-teamgrid" element={<AskTeamGridPage />} />
          <Route path="features/automatic-time-tracking" element={<AutomaticTimeTrackingPage />} />
          <Route path="features/productivity-scoring" element={<ProductivityScoringPage />} />
          <Route path="features/ai-work-summaries" element={<AIWorkSummariesPage />} />
          <Route path="features/app-website-tracking" element={<AppWebsiteTrackingPage />} />
          <Route path="features/hourly-breakdown" element={<HourlyBreakdownPage />} />
          <Route path="features/team-management" element={<TeamManagementPage />} />
          <Route path="features/project-tracking" element={<ProjectTrackingPage />} />
          <Route path="features/org-intelligence" element={<OrgIntelligencePage />} />
          <Route path="features/ai-powered-insights" element={<To to="/features/ask-teamgrid" />} />
          <Route path="features/performance-metrics" element={<To to="/features/productivity-scoring" />} />

          {/* solutions */}
          <Route path="solutions" element={<SolutionsPage />} />
          <Route path="solutions/remote-team-productivity" element={<RemoteTeamsPage />} />
          <Route path="solutions/hr-staffing-firms" element={<HRStaffingPage />} />
          <Route path="solutions/edtech-productivity-software" element={<EdTechPage />} />
          <Route path="solutions/accounting-firm-productivity" element={<AccountingPage />} />
          <Route path="solutions/project-based-teams" element={<ProjectBasedPage />} />
          <Route path="solutions/virtual-support-services" element={<VirtualSupportPage />} />
          <Route path="solutions/operations-execution-teams" element={<OperationsPage />} />
          <Route path="solutions/bpo-shared-services" element={<BPOPage />} />
          <Route path="solutions/contract-freelance-teams" element={<ContractFreelancePage />} />
          <Route path="solutions/client-driven-work" element={<ClientDrivenPage />} />
          <Route path="solutions/remote-distributed-teams" element={<To to="/solutions/remote-team-productivity" />} />
          <Route path="solutions/edtech-teams" element={<To to="/solutions/edtech-productivity-software" />} />
          <Route path="solutions/accounting-firms" element={<To to="/solutions/accounting-firm-productivity" />} />
          <Route path="solutions/operations-and-executions" element={<To to="/solutions/operations-execution-teams" />} />
          <Route path="solutions/client-driven-work-teams" element={<To to="/solutions/client-driven-work" />} />

          {/* comparisons */}
          <Route path="compare" element={<ComparePage />} />
          <Route path="compare/teamgrid-ai-vs-employee-monitoring-software" element={<WorkforcePlatformPage />} />
          <Route path="compare/teamgrid-ai-vs-we360-ai" element={<We360Page />} />
          <Route path="compare/teamgrid-ai-vs-insightful" element={<InsightfulPage />} />
          <Route path="compare/teamgrid-ai-vs-prohance" element={<ProHancePage />} />
          <Route path="compare/teamgrid-ai-vs-time-doctor" element={<TimeDoctorPage />} />
          <Route path="compare/teamgrid-ai-vs-hubstaff" element={<HubstaffPage />} />
          <Route path="compare/teamgrid-ai-vs-activtrak" element={<ActivTrakPage />} />
          <Route path="comparison/workforce-platform" element={<To to="/compare/teamgrid-ai-vs-employee-monitoring-software" />} />
          <Route path="comparison/we360" element={<To to="/compare/teamgrid-ai-vs-we360-ai" />} />
          <Route path="comparison/insightful" element={<To to="/compare/teamgrid-ai-vs-insightful" />} />
          <Route path="comparison/prohance" element={<To to="/compare/teamgrid-ai-vs-prohance" />} />
          <Route path="comparison/time-doctor" element={<To to="/compare/teamgrid-ai-vs-time-doctor" />} />
          <Route path="comparison/hubstaff" element={<To to="/compare/teamgrid-ai-vs-hubstaff" />} />
          <Route path="comparison/activtrak" element={<To to="/compare/teamgrid-ai-vs-activtrak" />} />

          {/* SEO landings */}
          <Route path="employee-monitoring-software" element={<EmployeeMonitoringSoftwarePage />} />
          <Route path="time-tracking-software" element={<TimeTrackingSoftwarePage />} />

          {/* company & content */}
          <Route path="about" element={<AboutPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog-post" element={<BlogPostPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="careers" element={<CareersPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="partners" element={<PartnersPage />} />

          {/* legal */}
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="terms" element={<TermsPage />} />

          {/* auth & account */}
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="invite" element={<InvitePage />} />
          <Route path="partner-login" element={<PartnerLoginPage />} />

          {/* post-submission */}
          <Route path="thanks-demo-request" element={<ThanksDemoRequestPage />} />
          <Route path="thanks-contact-us" element={<ThanksContactPage />} />

          <Route path="resource-optimizer" element={<ResourceOptimizerPage />} />
          <Route path="resource-optimizer/book" element={<ResourceOptimizerBookPage />} />

          {/* old Managed Services URLs kept working */}
          <Route path="managed-services" element={<To to="/resource-optimizer" />} />
          <Route path="managed-services/book" element={<To to="/resource-optimizer/book" />} />

          {/* legacy live-site URLs kept alive */}
          <Route path="contact-us" element={<To to="/contact" />} />
          <Route path="privacy-policy" element={<To to="/privacy" />} />
          <Route path="terms-of-service" element={<To to="/terms" />} />
          <Route path="home" element={<To to="/" />} />
          <Route path="index" element={<To to="/" />} />
          <Route path="landing" element={<To to="/" />} />
          <Route path="ai-workforce-intelligence" element={<To to="/" />} />
          <Route path="landing-alt" element={<To to="/" />} />
          <Route path="alternate" element={<To to="/" />} />
        </Route>
        {/* Only reachable if this component is mounted directly on a URL the
            manifest doesn't list; LandingSwitch hands unknown URLs to the host. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}
